import * as R from 'ramda';
import {
  add,
  sub,
  set,
  parse,
  format,
  subDays,
  addDays,
  isValid,
  isAfter,
  addYears,
  subWeeks,
  isBefore,
  getHours,
  subYears,
  addHours,
  subHours,
  endOfDay,
  getISODay,
  formatISO,
  addMonths,
  subMonths,
  getMinutes,
  addMinutes,
  subMinutes,
  startOfDay,
  startOfMonth,
  startOfISOWeek,
  differenceInDays,
  differenceInHours,
  intervalToDuration,
  formatDistanceToNow,
  differenceInMinutes,
  differenceInMilliseconds,
  isSameDay as isSameDayFns,
} from 'date-fns';
// constants
import * as GC from '../constants';
// helpers
import { toNumber } from './calc';
import { isPlural } from './string';
import { getAmousConfigByNameFromWindow } from './window';
import {
  parseISODuration,
  humanizeDuration,
  normalizeDuration,
} from './duration';
import {
  ifElse,
  isNumber,
  isString,
  isAllTrue,
  notEquals,
  isNilOrZero,
  isNilOrEmpty,
  isNotNilAndNotEmpty,
} from './helpers';
//////////////////////////////////////////////////

const getCurrentDate = () => new Date();

const getCurrentDay = () => format(new Date(), GC.DEFAULT_DATE_FORMAT);

const getCurrentDayWithTime = () => format(new Date(), GC.DEFAULT_DATE_TIME_FORMAT);

const getNewDate = (item) => new Date(item);

const isDateInstance = (item) => item instanceof Date;

const getCurrentDateWithFormat = (dateFormat = GC.DEFAULT_DATE_FORMAT) => format(new Date(), dateFormat);

const isValidDate = (item) => (
  isNotNilAndNotEmpty(item) ? isValid(new Date(item)) : false
);

const isValidTime = (time) => GC.TIME_REGEXP.test(time);

const isValidDateWithFormat = (item, dateFormat) => (
  isNotNilAndNotEmpty(item) ? isValid(parse(item, dateFormat, new Date())) : false
);

const getNowTimeDateObject = () => set(new Date(), { hours: 0, minutes: 0 });

const dateAddYearsFromCurrent = (years) => add(new Date(), { years });

const dateSubtractYearsFromCurrent = (years) => sub(new Date(), { years });

const getDateRange = (firstDate, secondDate, type = 'ms') => {
  const dateRange = [new Date(secondDate), new Date(firstDate)];

  const functionsMap = {
    d: differenceInDays,
    h: differenceInHours,
    m: differenceInMinutes,
    ms: differenceInMilliseconds,
  };

  return R.propOr(differenceInMilliseconds, type, functionsMap)(...dateRange);
};

const isSameDay = (firstDate, secondDate) => isSameDayFns(new Date(firstDate), new Date(secondDate));

// check this
const isAfterDate = (firstDate, secondDate) => isAfter(new Date(firstDate), new Date(secondDate));

const isBeforeDate = (firstDate, secondDate) =>
  isBefore(new Date(firstDate), new Date(secondDate));

const isBetween = (date, firstDate, secondDate, inclusion) => {
  const dt = new Date(date);
  const start = new Date(firstDate);
  const end = new Date(secondDate);
  const isInRange = isAfter(dt, start) && isBefore(dt, end);

  return inclusion ? isInRange || isSameDayFns(dt, start) || isSameDayFns(dt, end) : isInRange;
};

const toMidday = (date) => set(new Date(date), { hours: 12, minutes: 0, seconds: 0 });

const isMidnight = (date) => {
  const dateTime = format(new Date(date), 'MM:dd:yyyy HH:mm');

  return R.and(R.equals(getHours(dateTime), 0), R.equals(getMinutes(dateTime), 0));
};

const fromNow = (item) => formatDistanceToNow(new Date(item), { addSuffix: true });

const getStartOfMonthDay = () => format(startOfMonth(new Date()), GC.DEFAULT_DATE_FORMAT);

const addDateTime = (item, interval = 0, type = 'hours') => {
  if (R.not(isValidDate(item))) return item;

  const date = new Date(item);

  const map = {
    days: addDays,
    hours: addHours,
    years: addYears,
    months: addMonths,
    minutes: addMinutes,
  };

  return R.propOr(addHours, type, map)(date, interval);
};

const subtractDateTime = (item, interval = 0, type = 'hours') => {
  if (R.not(isValidDate(item))) return item;

  const date = new Date(item);

  const map = {
    days: subDays,
    hours: subHours,
    years: subYears,
    months: subMonths,
    minutes: subMinutes,
  };

  return R.propOr(subHours, type, map)(date, interval);
};

const addDateTimeWithFormat = (
  item,
  interval = 0,
  type = 'hours',
  dateFormat = GC.DEFAULT_DATE_TIME_FORMAT,
) => {
  if (R.and(R.equals(type, 'days'), R.gt(interval, 1000000))) return item;

  if (isValidDate(item)) return format(addDateTime(item, interval, type), dateFormat);

  return item;
};

const subtractDateTimeWithFormat = (
  item,
  interval = 0,
  type = 'hours',
  dateFormat = GC.DEFAULT_DATE_TIME_FORMAT,
) => {
  if (isValidDate(item)) return format(subtractDateTime(item, interval, type), dateFormat);

  return item;
};

const convertInstanceToLocalTime = (item) => (
  isValidDate(item) ? format(new Date(item), 'h:mm aa') : item
);

const convertInstanceToISOString = (item) => formatISO(new Date(item));

const createLocalDateTimeFormat = (dateFormat) => `${dateFormat} h:mm aa`;

const convertInstanceToDefaultDateFormat = (item) => format(
  isValidDate(item) ? new Date(item) : new Date(),
  GC.DEFAULT_DATE_FORMAT,
);

const convertInstanceToDefaultUserFormat = (item) => (
  isValidDate(item) ? format(new Date(item), GC.DEFAULT_USER_FORMAT) : item
);

const convertInstanceToDefaultDateTimeFormat = (item) => format(new Date(item), GC.DEFAULT_DATE_TIME_FORMAT);

const createTodayLocalDateTimeString = (dateFormat, timeValue) => (
  `${format(new Date(), dateFormat)} ${timeValue}`
);

const createLocalDateTimeFromInstanceOrISOString = (item, dateFormat = GC.DEFAULT_DATE_FORMAT) => (
  isValidDate(item) ? format(new Date(item), dateFormat) : item
);

const toEndOfDayFromDate = (item) => (
  isValidDate(item) ? format(endOfDay(new Date(item)), GC.DEFAULT_DATE_TIME_FORMAT) : item
);

const toStartOfDayFromDate = (item) => (
  isValidDate(item) ? format(startOfDay(new Date(item)), GC.DEFAULT_DATE_TIME_FORMAT) : item
);

const checkAndConvertMomentInstanceToFormattedDate = (item, format) => {
  if (isDateInstance(item)) {
    return createLocalDateTimeFromInstanceOrISOString(item, format);
  }

  return item;
};

const checkAndConvertStringToFormattedDate = (item, format) => {
  if (isString(item)) {
    return createLocalDateTimeFromInstanceOrISOString(item, format);
  }

  return item;
};

const checkAndConvertMomentInstanceOrStringToFormattedDate = (item, format) => {
  if (R.or(isString(item), isDateInstance(item))) {
    return createLocalDateTimeFromInstanceOrISOString(item, format);
  }

  return item;
};

const setMaxDate = (props, field) => {
  if (R.and(
    notEquals(field.type, 'calendar'),
    notEquals(field.type, 'datePicker'),
  )) return null;

  if (isNotNilAndNotEmpty(field.maxDate)) return field.maxDate;

  if (R.and(
      isNotNilAndNotEmpty(field.maxDateField),
      isNotNilAndNotEmpty(R.path(['formValues', field.maxDateField], props)),
    )) {
    return getNewDate(props.formValues[field.maxDateField]);
  }

  return null;
};

const setMinDate = (props, field) => {
  if (R.and(
    notEquals(field.type, 'calendar'),
    notEquals(field.type, 'datePicker'),
  )) return null;

  if (isNotNilAndNotEmpty(field.minDate)) return field.minDate;

  if (R.and(
      isNotNilAndNotEmpty(field.minDateField),
      isNotNilAndNotEmpty(R.path(['formValues', field.minDateField], props)),
    )) {
    return getNewDate(props.formValues[field.minDateField]);
  }

  return null;
};

const getFormattedDurationFromMinutes = (min) => {
  const endDate = new Date(0);

  endDate.setMinutes(min);

  const dur = intervalToDuration({ start: new Date(0), end: endDate });

  const { days = 0, hours = 0, minutes = 0 } = dur;

  const dayStr = ifElse(R.equals(days, 0), '', `${days} ${isPlural(days, 'day')}  `);
  const hourStr = ifElse(R.equals(hours, 0), '', `${hours} ${isPlural(hours, 'hour')}  `);
  const minuteStr = ifElse(R.equals(minutes, 0), '', `${minutes} ${isPlural(minutes, 'min')}  `);

  return R.or(`${dayStr}${hourStr}${minuteStr}`, '0 min');
};

const getFormattedDurationFromString = (str, humanize) => {
  const dur = parseISODuration(str);
  const normalized = normalizeDuration(dur);

  if (humanize) {
    return humanizeDuration(normalized);
  }

  const { days = 0, hours = 0, years = 0, months = 0, minutes = 0 } = normalized;

  const dayStr = ifElse(R.equals(days, 0), '', `${days} ${isPlural(days, 'day')}  `);
  const hourStr = ifElse(R.equals(hours, 0), '', `${hours} ${isPlural(hours, 'hour')}  `);
  const yearStr = ifElse(R.equals(years, 0), '', `${years} ${isPlural(years, 'year')}  `);
  const monthStr = ifElse(R.equals(months, 0), '', `${months} ${isPlural(months, 'month')}  `);
  const minuteStr = ifElse(R.equals(minutes, 0), '', `${minutes} ${isPlural(minutes, 'min')}  `);

  return R.or(`${yearStr}${monthStr}${dayStr}${hourStr}${minuteStr}`, '0 min');
};

const addDurationToDate = (date, duration) => {
  if (R.or(isNilOrEmpty(duration), R.not(isValidDate(date)))) return date;

  const durationObj = parseISODuration(duration);

  return add(new Date(date), durationObj);
};

const getIntervalToDuration = (start, end) => {
  const { days = 0, hours = 0 } = intervalToDuration({ start, end });

  if (R.equals(days, 0)) return `${hours} ${isPlural(hours, 'hour')}`;

  if (R.equals(hours, 0)) return `${days} ${isPlural(days, 'day')}`;

  return `${days} ${isPlural(days, 'day')}  ${hours} ${isPlural(hours, 'hour')}`;
};

const getDateTimeFormat = (timeSelection) => (
  ifElse(
    R.equals(timeSelection, true),
    GC.DEFAULT_DATE_TIME_FORMAT,
    GC.DEFAULT_DATE_FORMAT,
  )
);

const getDateTimeFormatFromConfig = (formatType = 'dateTime') => {
  const dateTimeFormatConfig = getAmousConfigByNameFromWindow(GC.GENERAL_BRANCH_DATE_TIME_FORMAT);

  const formatDataObject = R.pathOr(
    R.prop(GC.DATE_TIME_FORMAT_US, GC.dateTimeFormatMap),
    [dateTimeFormatConfig],
    GC.dateTimeFormatMap,
  );

  return R.path([formatType, 'format'], formatDataObject);
};

const getDateTimeFormatForMui = (timeSelection) => {
  const timeFormat = getAmousConfigByNameFromWindow(GC.GENERAL_BRANCH_DATE_TIME_FORMAT);
  const ampm = R.equals(timeFormat, GC.DATE_TIME_FORMAT_US);
  const format = ifElse(
    R.equals(timeSelection, true),
    ifElse(
      ampm,
      GC.DATE_TIME_FORMAT_AM_PM_UPPERCASE,
      GC.DEFAULT_US_MILITARY_DATE_TIME_FORMAT,
    ),
    GC.DEFAULT_DATE_FORMAT,
  );

  return {
    ampm,
    format,
  };
};

const getSplittedDateTime = (value) => {
  if (R.and(isNotNilAndNotEmpty(value), isValidDate(value))) {
    const splitted = R.split(' ', value);
    const date = R.head(splitted);
    const time = R.nth(1, splitted);
    const ampm = R.nth(2, splitted);

    return { date, time, ampm };
  }

  return value;
};

const setTimeToDateTime = (value, time) => {
  if (isAllTrue(isNotNilAndNotEmpty(value), isNotNilAndNotEmpty(time), isValidDate(value))) {
    const splitted = R.split(' ', value);
    const date = R.head(splitted);

    const newValue = R.toUpper(`${date} ${time}`);

    if (isValidDate(newValue)) return newValue;

    return value;
  }

  return value;
};

const convertTimeToFormat = (timeString, format = GC.DEFAULT_TIME_FORMAT) => {
  if (isNilOrEmpty(timeString)) return timeString;

  return checkAndConvertStringToFormattedDate(
    `${getCurrentDay()} ${timeString}`,
    format,
  );
};

const getFormattedOperationHoursFromStop = ({ operationHour }) => {
  if (isNilOrEmpty(operationHour)) return operationHour;

  return R.map(
    (entity) => R.mergeRight(entity, {
      [GC.FIELD_TIME_TO]: convertTimeToFormat(
        R.path([GC.FIELD_TIME_TO], entity),
        GC.DEFAULT_TIME_FORMAT,
      ),
      [GC.FIELD_TIME_FROM]: convertTimeToFormat(
        R.path([GC.FIELD_TIME_FROM], entity),
        GC.DEFAULT_TIME_FORMAT,
      ),
    }),
    operationHour,
  );
};

const convertTimeToConfigFormat = (time) => {
  const format = getDateTimeFormatFromConfig('time');

  return convertTimeToFormat(time, format);
};

const convertDateTimeToConfigFormat = (dateTime) => {
  const format = getDateTimeFormatFromConfig();

  return checkAndConvertMomentInstanceOrStringToFormattedDate(dateTime, format);
};

const convertOperationHoursToConfigFormat = R.map((entity) => ({
  ...entity,
  [GC.FIELD_TIME_TO]: convertTimeToConfigFormat(R.prop(GC.FIELD_TIME_TO, entity)),
  [GC.FIELD_TIME_FROM]: convertTimeToConfigFormat(R.prop(GC.FIELD_TIME_FROM, entity)),
}));

const convertOperationHoursToDefaultFormat = R.map((entity) => ({
  ...entity,
  [GC.FIELD_TIME_TO]: convertTimeToFormat(R.prop(GC.FIELD_TIME_TO, entity)),
  [GC.FIELD_TIME_FROM]: convertTimeToFormat(R.prop(GC.FIELD_TIME_FROM, entity)),
}));

const convertMinutesToHoursAndMinutes = (min) => {
  const defaultObject = {
    hours: 0,
    minutes: 0,
    shortString: '0h 0m',
    longString: '0 hour(s) 0 minute(s)',
  };

  if (isNilOrZero(min)) return defaultObject;

  const minNumber = toNumber(min);

  if (R.not(isNumber(minNumber))) return defaultObject;

  const hours = R.divide(minNumber, 60);
  const rHours = Math.floor(hours);
  const minutes = R.multiply(R.subtract(hours, rHours), 60);
  const rMinutes = Math.round(minutes);

  return {
    hours: rHours,
    minutes: rMinutes,
    shortString: `${rHours}h ${rMinutes}m`,
    longString: `${rHours} hour(s) ${rMinutes} minute(s)`,
  };
};

const getModuloFromHours = (hours, rHours) => {
  if (R.lt(hours, 1)) return hours;

  return R.modulo(hours, rHours);
};

const convertHoursToHoursAndMinutes = (h) => {
  const defaultObject = {
    hours: 0,
    minutes: 0,
    shortString: '0h 0m',
    longString: '0 hour(s) 0 minute(s)',
  };

  if (isNilOrZero(h)) return defaultObject;

  const hNumber = toNumber(h);

  if (R.not(isNumber(hNumber))) return defaultObject;


  const rHours = Math.floor(hNumber);
  const modulo = getModuloFromHours(hNumber, rHours);
  const minutes = R.multiply(modulo, 60);
  const rMinutes = Math.ceil(minutes);

  return {
    hours: rHours,
    minutes: rMinutes,
    shortString: `${rHours}h ${rMinutes}m`,
    longString: `${rHours} hour(s) ${rMinutes} minute(s)`,
  };
};

const convertDateTimeToConfigTimeZone = (dateTime, showTimeZoneName = true) => {
  if (isNilOrEmpty(dateTime)) return dateTime;

  const twoDigitFormat = '2-digit';
  const timeZone = getAmousConfigByNameFromWindow(GC.GENERAL_BRANCH_TIMEZONE);

  return getNewDate(dateTime).toLocaleString('en-US', {
    timeZone,
    year: 'numeric',
    day: twoDigitFormat,
    hour: twoDigitFormat,
    month: twoDigitFormat,
    minute: twoDigitFormat,
    timeZoneName: ifElse(showTimeZoneName, 'short', undefined),
  });
};

const formatStopDateTimeValues = (stop) => R.mergeRight(stop, {
  [GC.FIELD_LOAD_EVENT_LATE_TIME]: convertTimeToFormat(
    R.path([GC.FIELD_LOAD_EVENT_LATE_TIME], stop),
    GC.DEFAULT_TIME_FORMAT,
  ),
  [GC.FIELD_LOAD_EVENT_LATE_DATE]: checkAndConvertStringToFormattedDate(
    R.path([GC.FIELD_LOAD_EVENT_LATE_DATE], stop),
    GC.DEFAULT_DATE_TIME_FORMAT,
  ),
  [GC.FIELD_LOAD_EVENT_EARLY_TIME]: convertTimeToFormat(
    R.path([GC.FIELD_LOAD_EVENT_EARLY_TIME], stop),
    GC.DEFAULT_TIME_FORMAT,
  ),
  [GC.FIELD_LOAD_EVENT_EARLY_DATE]: checkAndConvertStringToFormattedDate(
    R.path([GC.FIELD_LOAD_EVENT_EARLY_DATE], stop),
    GC.DEFAULT_DATE_TIME_FORMAT,
  ),
  [GC.FIELD_LOAD_APPOINTMENT_DATE]: checkAndConvertStringToFormattedDate(
    R.path([GC.FIELD_LOAD_APPOINTMENT_DATE], stop),
    GC.DEFAULT_DATE_FORMAT,
  ),
  [GC.FIELD_LOAD_APPOINTMENT_LATE_TIME]: isNilOrEmpty(R.path([GC.FIELD_LOAD_APPOINTMENT_DATE], stop)) ?
    null : convertTimeToFormat(
      R.path([GC.FIELD_LOAD_APPOINTMENT_LATE_TIME], stop),
      GC.DEFAULT_TIME_FORMAT,
    ),
  [GC.FIELD_LOAD_APPOINTMENT_EARLY_TIME]: isNilOrEmpty(R.path([GC.FIELD_LOAD_APPOINTMENT_DATE], stop)) ?
    null : convertTimeToFormat(
      R.path([GC.FIELD_LOAD_APPOINTMENT_EARLY_TIME], stop),
      GC.DEFAULT_TIME_FORMAT,
    ),
  [GC.FIELD_OPERATION_HOUR]: getFormattedOperationHoursFromStop(stop),
  [GC.FIELD_EARLY_DATE]: checkAndConvertStringToFormattedDate(
    R.path([GC.FIELD_EARLY_DATE], stop),
    GC.DEFAULT_DATE_TIME_FORMAT,
  ),
  [GC.FIELD_LATE_DATE]: checkAndConvertStringToFormattedDate(
    R.path([GC.FIELD_LATE_DATE], stop),
    GC.DEFAULT_DATE_TIME_FORMAT,
  ),
});

const getValuesWithConvertedDateTimeFields = ({ values, dateTimeFields }) => {
  if (isNilOrEmpty(dateTimeFields)) return values;

  const getConvertedDate = (date) => {
    if (isValidDate(date)) return convertInstanceToDefaultDateTimeFormat(date);

    return date;
  };

  let valuesWithConvertedDateTimeFields = values;

  dateTimeFields.forEach((field) => {
    valuesWithConvertedDateTimeFields = R.assoc(
      field,
      getConvertedDate(R.pathOr('', [field], values)),
      valuesWithConvertedDateTimeFields,
    );
  });

  return valuesWithConvertedDateTimeFields;
};
const getDayOfThisWeekByDay = (day) => {
  const startOfWeek = startOfISOWeek(new Date());

  return addDays(startOfWeek, GC.dayToIsoDayNumberMap[day] - 1);
};

const getDayOfWeek = (date) => getNewDate(date).getDay();

const getDayOfPreviousWeekByDay = (day) => {
  const date = subWeeks(new Date(), 1);
  const startOfWeek = startOfISOWeek(new Date(date));

  return addDays(startOfWeek, GC.dayToIsoDayNumberMap[day] - 1);
};

const getDatesOfPreviousPeriodByDays = (fromDay, toDay) => {
  const today = new Date();

  const todayIso = getISODay(today);
  const toIso = GC.dayToIsoDayNumberMap[toDay];
  const fromIso = GC.dayToIsoDayNumberMap[fromDay];

  const baseDate = R.gt(todayIso, toIso) ? today : subWeeks(today, 1);

  const startWeek = startOfISOWeek(baseDate);
  const to = addDays(startWeek, toIso - 1);

  const diff = R.lt(fromIso, toIso) ? R.subtract(fromIso, toIso) : R.subtract(fromIso, toIso) - 7;
  const from = addDays(to, diff);

  return { dateTo: convertInstanceToDefaultDateFormat(to), dateFrom: convertInstanceToDefaultDateFormat(from) };
};

const getLocalTimeFromDate = (date) => createLocalDateTimeFromInstanceOrISOString(date, 'h:mm a');

const getTimeByConfigFormat = (date) => {
  const format = getDateTimeFormatFromConfig('time');

  if (isValidDate(date)) {
    return createLocalDateTimeFromInstanceOrISOString(date, format);
  }

  return date;
};

const getDaysFromNowToDate = (date) => {
  const date1 = new Date();
  const date2 = new Date(date);
  const diffTime = Math.abs(date2 - date1);

  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
};

const getDaysFromMilliseconds = (ms) => ms / 86400000;

const groupListByDate = R.compose(
  R.map(([key, value]) => ({ title: key, list: value })),
  R.toPairs,
  R.groupBy((obj) =>
    createLocalDateTimeFromInstanceOrISOString(obj.date, 'MMMM d, yyyy'),
  ),
);

export {
  fromNow,
  toMidday,
  isBetween,
  isSameDay,
  isMidnight,
  setMinDate,
  setMaxDate,
  getNewDate,
  isAfterDate,
  isValidTime,
  addDateTime,
  isValidDate,
  isBeforeDate,
  getDateRange,
  getDayOfWeek,
  getCurrentDay,
  getCurrentDate,
  isDateInstance,
  groupListByDate,
  subtractDateTime,
  addDurationToDate,
  getDateTimeFormat,
  setTimeToDateTime,
  getStartOfMonthDay,
  toEndOfDayFromDate,
  getSplittedDateTime,
  convertTimeToFormat,
  getLocalTimeFromDate,
  toStartOfDayFromDate,
  getDaysFromNowToDate,
  getNowTimeDateObject,
  getDayOfThisWeekByDay,
  getCurrentDayWithTime,
  getTimeByConfigFormat,
  isValidDateWithFormat,
  addDateTimeWithFormat,
  getIntervalToDuration,
  dateAddYearsFromCurrent,
  getDaysFromMilliseconds,
  getDateTimeFormatForMui,
  getCurrentDateWithFormat,
  formatStopDateTimeValues,
  convertTimeToConfigFormat,
  getDayOfPreviousWeekByDay,
  createLocalDateTimeFormat,
  convertInstanceToISOString,
  subtractDateTimeWithFormat,
  convertInstanceToLocalTime,
  getDateTimeFormatFromConfig,
  dateSubtractYearsFromCurrent,
  convertHoursToHoursAndMinutes,
  convertDateTimeToConfigFormat,
  getFormattedDurationFromString,
  createTodayLocalDateTimeString,
  getDatesOfPreviousPeriodByDays,
  convertDateTimeToConfigTimeZone,
  convertMinutesToHoursAndMinutes,
  getFormattedDurationFromMinutes,
  convertInstanceToDefaultUserFormat,
  getFormattedOperationHoursFromStop,
  convertInstanceToDefaultDateFormat,
  convertOperationHoursToConfigFormat,
  getValuesWithConvertedDateTimeFields,
  convertOperationHoursToDefaultFormat,
  checkAndConvertStringToFormattedDate,
  convertInstanceToDefaultDateTimeFormat,
  createLocalDateTimeFromInstanceOrISOString,
  checkAndConvertMomentInstanceToFormattedDate,
  checkAndConvertMomentInstanceOrStringToFormattedDate,
};
